<template>
  <span
    class="kds-avatar"
    :class="[
      `kds-avatar--${segment}`,
      `kds-avatar--size-${size}`,
      { 'kds-avatar--speaking': speaking && !reducedMotion }
    ]"
    :style="{
      width: `${size}px`,
      height: `${size}px`,
      backgroundColor: fallbackColor
    }"
  >
    <img
      v-if="resolvedSrc && !imageError"
      class="kds-avatar__img"
      :src="resolvedSrc"
      :alt="alt ?? ''"
      @error="onImageError"
    />
    <span
      v-if="speaking"
      class="kds-avatar__dot"
      :class="{ 'kds-pulse-dot': !reducedMotion }"
    />
  </span>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { SEGMENT_TOKEN } from '@/kds/tokens';
import type { KdsAvatarSize, PersonaSegment } from '@/kds/types';
import { useReducedMotion } from '@/composables/useReducedMotion';

interface Props {
  segment: PersonaSegment;
  size?: KdsAvatarSize;
  speaking?: boolean;
  src?: string;
  alt?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 48,
  speaking: false,
  src: undefined,
  alt: undefined
});

const imageError = ref(false);
const reducedMotion = useReducedMotion();

const resolvedSrc = computed(
  () => props.src ?? `/assets/persona-avatar-${props.segment}.svg`
);

const fallbackColor = computed(() => SEGMENT_TOKEN[props.segment]);

function onImageError() {
  imageError.value = true;
}
</script>

<style scoped lang="scss">
.kds-avatar {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-pill);
  overflow: visible;
  background-clip: padding-box;
  flex-shrink: 0;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--r-pill);
    display: block;
  }

  &--speaking {
    box-shadow: var(--shadow-signal);
  }

  &__dot {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 10px;
    height: 10px;
    border-radius: var(--r-pill);
    background: var(--signal);
    border: 2px solid var(--paper);
  }
}
</style>
